// One form for authoring a template, used for both creating and editing.
//
// There were two before, in two visual languages: the create panel rolled its
// own field primitives in a four-column grid, while the edit card used the
// house `Block` + `Field` grid. Same object, same fields, two screens — and the
// create one carried the bugs, because fixes landed on whichever the author
// happened to be looking at.
//
// Grouped into sections with the rarely-touched half behind Advanced, because
// the flat version put roughly 27 controls on screen at once with no ordering
// signal, and validation that only spoke after submit.

import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { Block } from '~/team_tools/accounts/Block'
import { CheckField, CustomField, SelectField, TextField } from './Field'
import { NAME_MAX, NAME_PLACEHOLDERS, TemplateFormState as FormState, previewName } from './templateFormState'
import { LootPool, Options, lootDrawLabel } from './templatesApi'

const Err = ({ message }: { message?: string }) =>
    message ? <span className='text-[10px] text-red-400'>{message}</span> : null

export const TemplateFormFields = ({
    form,
    setForm,
    options,
    errors,
    disabled,
}: {
    form: FormState
    setForm: (updater: (prev: FormState) => FormState) => void
    options: Options
    errors: Record<string, string>
    disabled?: boolean
}) => {
    const [advanced, setAdvanced] = useState(false)

    const set = (field: keyof FormState) => (value: any) => setForm((prev) => ({ ...prev, [field]: value }))

    const toggleRegion = (slot: string, on: boolean) =>
        setForm((prev) => ({
            ...prev,
            deploy_slot_regions: on
                ? [...prev.deploy_slot_regions, slot]
                : prev.deploy_slot_regions.filter((r) => r !== slot),
        }))

    const setPool = (currency: string, patch: Partial<LootPool>) =>
        setForm((prev) => {
            const pools = [...prev.loot_pools]
            const i = pools.findIndex((p) => p.currency === currency)
            if (i === -1) {
                pools.push({
                    currency,
                    amount: '0',
                    reward_min: '0',
                    reward_max: '0',
                    auto_add_from_spawn_fee: '0',
                    ...patch,
                })
            } else {
                pools[i] = { ...pools[i], ...patch }
            }
            return { ...prev, loot_pools: pools }
        })

    const togglePool = (currency: string, on: boolean) =>
        setForm((prev) => ({
            ...prev,
            loot_pools: on
                ? [
                      ...prev.loot_pools,
                      {
                          currency,
                          amount: '0',
                          reward_min: '0',
                          reward_max: '0',
                          auto_add_from_spawn_fee: '0',
                      },
                  ]
                : prev.loot_pools.filter((p) => p.currency !== currency),
        }))

    const pool = (currency: string) => form.loot_pools.find((p) => p.currency === currency)

    // Which regions a server deployed from this template will report. Derived
    // here the same way the backend derives it, and shown rather than asked:
    // the two used to be separate fields and staff had to keep them in step by
    // hand, with a mismatch silently matching no servers at all.
    const matched = Array.from(
        new Set(
            form.deploy_slot_regions
                .map((slot) => options.slot_regions.find((r) => r.id === slot)?.server_region)
                .filter(Boolean) as string[]
        )
    )

    // The template's own values, so the preview shows the name this template
    // will actually produce rather than a sample of somebody else's.
    const preview = previewName(form.server_name_template, {
        mapName: options.maps.find((m) => m.id === Number(form.map_id))?.name,
        fee: form.fee,
        region: matched[0],
    })

    return (
        <>
            <Block header='identity' align='left'>
                <TextField
                    label='Template name'
                    value={form.name}
                    onChange={set('name')}
                    disabled={disabled}
                    placeholder='Infinite US'
                />
                <CustomField label=''>
                    <span className='text-[10px] text-white/40'>Label for staff. Not the name players see.</span>
                    <Err message={errors.name} />
                </CustomField>

                <CheckField
                    label='Enabled'
                    checked={form.enabled}
                    onChange={set('enabled')}
                    disabled={disabled}
                    hint='Off means nothing deploys or applies it.'
                />
                <CheckField
                    label='Activate on apply'
                    checked={form.activate_on_apply}
                    onChange={set('activate_on_apply')}
                    disabled={disabled}
                    hint='Put the server live as soon as it is configured.'
                />
            </Block>

            {/* Folded by default, but a save error inside a folded block was
                invisible: "Some fields need fixing" and nothing to fix. Block
                reads defaultOpen once, so the key remounts it open. */}
            <Block key={(errors.deploy_slot_regions) ? 'placement-err' : 'placement'} header='placement' align='left' collapsible defaultOpen={Boolean(errors.deploy_slot_regions)}>
                <CustomField
                    label='Deploy into slots'
                    hint='Pick every region this template may deploy into; you choose one when deploying.'
                >
                    <div className='flex flex-row flex-wrap gap-3'>
                        {options.slot_regions.map((r) => (
                            <label key={r.id} className='flex flex-row items-center gap-2 text-sm cursor-pointer'>
                                <input
                                    type='checkbox'
                                    checked={form.deploy_slot_regions.includes(r.id)}
                                    disabled={disabled}
                                    onChange={(e) => toggleRegion(r.id, e.target.checked)}
                                    className='accent-violet-500 disabled:opacity-40'
                                />
                                <span>{r.label}</span>
                                <span className='text-[10px] opacity-40'>{r.id}</span>
                            </label>
                        ))}
                    </div>
                    <Err message={errors.deploy_slot_regions} />
                </CustomField>

                <CustomField
                    label='Matches servers reporting'
                    hint='Derived from the slot ranges above. South America reports as US.'
                >
                    <span className={matched.length ? '' : 'opacity-40'}>
                        {matched.join(', ') || 'nothing yet, pick a region above'}
                    </span>
                </CustomField>
            </Block>

            <Block key={(errors.server_name_template || errors.fee || errors.max_players || errors.password) ? 'applies-err' : 'applies'} header='what it applies' align='left' collapsible defaultOpen={Boolean(errors.server_name_template || errors.fee || errors.max_players || errors.password)}>
                <TextField
                    label='Server name'
                    value={form.server_name_template}
                    onChange={set('server_name_template')}
                    disabled={disabled}
                />
                <CustomField label='Renders as'>
                    <span className={preview.unknown.length || preview.tooLong ? 'text-red-400' : 'opacity-70'}>
                        {preview.rendered || '-'}
                    </span>
                    {!!preview.unknown.length && (
                        <span className='text-[10px] text-red-400'>
                            Unknown placeholder(s): {preview.unknown.map((u) => `{${u}}`).join(', ')}. Allowed:{' '}
                            {NAME_PLACEHOLDERS.map((p) => `{${p}}`).join(' ')}
                        </span>
                    )}
                    {preview.tooLong && (
                        <span className='text-[10px] text-red-400'>
                            {preview.rendered.length} characters. The server name allows {NAME_MAX}.
                        </span>
                    )}
                    {!preview.unknown.length && !preview.tooLong && (
                        <span className='text-[10px] text-white/40'>
                            Placeholders: {NAME_PLACEHOLDERS.map((p) => `{${p}}`).join(' ')}
                        </span>
                    )}
                    <Err message={errors.server_name_template} />
                </CustomField>

                <SelectField
                    label='Map'
                    value={form.map_id}
                    disabled={disabled}
                    options={options.maps.map((m) => ({ value: m.id, label: m.name }))}
                    onChange={(v) => set('map_id')(Number(v))}
                />

                <TextField
                    label='Spawn fee'
                    value={form.fee}
                    onChange={set('fee')}
                    disabled={disabled}
                    placeholder='0.10'
                />
                <CustomField label=''>
                    <span className='text-[10px] text-white/40'>Blank or 0 makes it a free server.</span>
                    <Err message={errors.fee} />
                </CustomField>

                <TextField
                    label='Max players'
                    value={form.max_players}
                    onChange={set('max_players')}
                    disabled={disabled}
                    placeholder='no cap'
                />
                <CustomField label=''>
                    <Err message={errors.max_players} />
                </CustomField>

                <TextField
                    label='Password'
                    value={form.password}
                    onChange={set('password')}
                    disabled={disabled}
                    placeholder='public'
                />
                <CustomField label=''>
                    <span className='text-[10px] text-white/40'>
                        Set one and the server is private. Private servers earn no points.
                    </span>
                    <Err message={errors.password} />
                </CustomField>
            </Block>

            <Block key={(errors.loot_update_frequency || errors.loot_max_spawn_points) ? 'loot-err' : 'loot'} header='loot' align='left' collapsible defaultOpen={Boolean(errors.loot_update_frequency || errors.loot_max_spawn_points)}>
                <TextField
                    label='Spawn cycle (s)'
                    value={form.loot_update_frequency}
                    onChange={set('loot_update_frequency')}
                    disabled={disabled}
                    placeholder='300'
                />
                <CustomField label=''>
                    <Err message={errors.loot_update_frequency} />
                </CustomField>

                <TextField
                    label='Spawn points'
                    value={form.loot_max_spawn_points}
                    onChange={set('loot_max_spawn_points')}
                    disabled={disabled}
                    placeholder='20'
                />
                <CustomField label=''>
                    {String(form.loot_max_spawn_points).trim() === '0' ? (
                        <span className='text-[10px] text-amber-400'>
                            Zero spawn points disables loot on this server entirely.
                        </span>
                    ) : (
                        <span className='text-[10px] text-white/40'>Spawn points populated per cycle.</span>
                    )}
                    <Err message={errors.loot_max_spawn_points} />
                </CustomField>

                {options.currencies.map((currency) => {
                    const p = pool(currency)
                    return (
                        <CustomField key={currency} label={`${currency} pool`}>
                            <label className='flex flex-row items-center gap-2 text-sm cursor-pointer'>
                                <input
                                    type='checkbox'
                                    checked={!!p}
                                    disabled={disabled}
                                    onChange={(e) => togglePool(currency, e.target.checked)}
                                    className='accent-violet-500 disabled:opacity-40'
                                />
                                <span className='opacity-60 text-xs'>
                                    Create a {currency} reward pool on each deployed server
                                </span>
                            </label>
                            {p && (
                                <div className='grid grid-cols-[max-content,1fr] gap-x-3 gap-y-1 pt-2'>
                                    <TextField
                                        label='Starting balance'
                                        value={p.amount}
                                        disabled={disabled}
                                        onChange={(v) => setPool(currency, { amount: v })}
                                    />
                                    <TextField
                                        label='Reward min'
                                        value={p.reward_min}
                                        disabled={disabled}
                                        onChange={(v) => setPool(currency, { reward_min: v })}
                                    />
                                    <TextField
                                        label='Reward max'
                                        value={p.reward_max}
                                        disabled={disabled}
                                        onChange={(v) => setPool(currency, { reward_max: v })}
                                    />
                                    {/* Only the USDC pool is topped up, and only from the
                                        USDC actually paid (D-020). A POINT top-up did
                                        nothing, so it is not offered. */}
                                    {currency === 'USDC' && (
                                        <TextField
                                            label='Top-up share'
                                            value={p.auto_add_from_spawn_fee}
                                            disabled={disabled}
                                            onChange={(v) => setPool(currency, { auto_add_from_spawn_fee: v })}
                                        />
                                    )}
                                    <span className='col-span-2 text-[10px] opacity-60'>
                                        {lootDrawLabel(currency, p.reward_min, p.reward_max)}
                                    </span>
                                </div>
                            )}
                        </CustomField>
                    )
                })}
            </Block>

            <Block header='tournaments' align='left' collapsible defaultOpen={false}>
                <CustomField label='Counts towards' hint='Server-scoped tournaments a deployed server should feed.'>
                    {!options.tournaments.length && (
                        <span className='text-xs opacity-40'>No tournaments are currently running or upcoming.</span>
                    )}
                    <div className='flex flex-row flex-wrap gap-3'>
                        {options.tournaments.map((t) => (
                            <label key={t.id} className='flex flex-row items-center gap-2 text-sm cursor-pointer'>
                                <input
                                    type='checkbox'
                                    checked={form.tournaments.includes(t.id)}
                                    disabled={disabled}
                                    onChange={(e) =>
                                        set('tournaments')(
                                            e.target.checked
                                                ? [...form.tournaments, t.id]
                                                : form.tournaments.filter((id) => id !== t.id)
                                        )
                                    }
                                    className='accent-violet-500 disabled:opacity-40'
                                />
                                <span>{t.name}</span>
                            </label>
                        ))}
                    </div>
                </CustomField>
            </Block>

            {/* A sibling of the Blocks, not a field: it belongs to the form as
                a whole rather than to any one group. */}
            <div>
                <button
                    onClick={() => setAdvanced((a) => !a)}
                    className='flex flex-row items-center gap-2 py-1 text-xs uppercase tracking-widest opacity-60 hover:opacity-100'
                >
                    <FontAwesomeIcon icon={advanced ? faChevronDown : faChevronRight} />
                    Advanced
                    {!advanced && (
                        <span className='normal-case tracking-normal opacity-60'>
                            player requirements
                            {form.check_requirements ? ': on' : ': off'}
                        </span>
                    )}
                </button>
            </div>

            {advanced && (
                <Block header='player requirements' align='left' collapsible defaultOpen={false}>
                    <CheckField
                        label='Check requirements'
                        checked={form.check_requirements}
                        onChange={set('check_requirements')}
                        disabled={disabled}
                        hint='Off means the limits below are ignored entirely.'
                    />
                    <CustomField label=''>
                        <span className='text-[10px] text-white/40'>
                            Gates who may join. Leave a box empty for no limit on that end.
                        </span>
                    </CustomField>

                    {(
                        [
                            ['min_sessions', 'Min sessions'],
                            ['max_sessions', 'Max sessions'],
                            ['min_kills', 'Min kills'],
                            ['max_kills', 'Max kills'],
                        ] as Array<[keyof FormState, string]>
                    ).map(([field, label]) => (
                        <TextField
                            key={String(field)}
                            label={label}
                            value={form[field] as string}
                            onChange={set(field)}
                            placeholder='any'
                            // Greyed out rather than hidden when the toggle is
                            // off: the values still exist and turning the check
                            // back on will use them.
                            disabled={disabled || !form.check_requirements}
                        />
                    ))}
                    <CustomField label=''>
                        <Err message={errors.min_sessions || errors.max_sessions} />
                        <Err message={errors.min_kills || errors.max_kills} />
                        <Err message={errors.__all__} />
                    </CustomField>
                </Block>
            )}
        </>
    )
}
